// Lead capture client for the EVs-for-Idiots concierge funnel.
//
// Posts leads to the shared Node/RDS backend (the same Express app that
// serves api.marketcast.co; EV routes live under /api/evs, see
// stock-api-node/server/routes/evs.js). If the backend is unreachable, the
// table isn't created yet, or no base URL is configured, the lead is queued
// in localStorage and retried on the next page load. The UI therefore always
// succeeds for the user and nothing is lost in "demo mode".
//
// Configure the backend by setting REACT_APP_LEADS_API_BASE at build time.
// Defaults to the production API host.

import { track } from './track';

const API_BASE = (process.env.REACT_APP_LEADS_API_BASE || 'https://api.marketcast.co').replace(/\/$/, '');
const ENDPOINT = `${API_BASE}/api/evs/leads`;
const QUEUE_KEY = 'evs_pending_leads';

// Web3Forms: the launch lead backend. If a key is set, leads are emailed to
// the address tied to that key (no server needed). Get a key at web3forms.com
// and set REACT_APP_WEB3FORMS_KEY in the Vercel project env. When unset, leads
// fall back to the RDS API endpoint above.
const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || '';
const WEB3FORMS_URL = 'https://api.web3forms.com/submit';

function readQueue() {
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}

function writeQueue(arr) {
  try {
    // Cap the queue so a permanently-offline backend can't grow it forever.
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(arr.slice(-50)));
  } catch (e) {
    /* storage full / disabled, nothing else we can do */
  }
}

function queueLead(payload) {
  const q = readQueue();
  q.push({ ...payload, _queuedAt: new Date().toISOString() });
  writeQueue(q);
}

async function postLead(payload) {
  // Launch path: Web3Forms (emails the lead to you, no server). Flatten the
  // payload into readable fields so the email is legible, and stringify the
  // structured `details` (quiz answers, matched EVs, dealer quote, etc.).
  if (WEB3FORMS_KEY) {
    const { details, ...rest } = payload;
    const res = await fetch(WEB3FORMS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        access_key: WEB3FORMS_KEY,
        subject: `New EV lead: ${payload.lead_type || 'lead'}${payload.zip ? ` (${payload.zip})` : ''}`,
        from_name: 'EVs for Idiots',
        ...rest,
        details: details ? JSON.stringify(details) : undefined,
      }),
      keepalive: true,
    });
    if (!res.ok) throw new Error(`web3forms post failed: ${res.status}`);
    return res.json().catch(() => ({ ok: true }));
  }

  // Fallback: the RDS API endpoint.
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    // keepalive lets the request survive a navigation right after submit.
    keepalive: true,
  });
  if (!res.ok) throw new Error(`lead post failed: ${res.status}`);
  return res.json().catch(() => ({ ok: true }));
}

/**
 * Submit a lead. Always resolves (never throws) so callers can show a
 * success state unconditionally.
 *
 * @param {Object} lead
 * @param {string} lead.email                 required
 * @param {string} [lead.name]
 * @param {string} [lead.phone]
 * @param {string} [lead.zip]
 * @param {string} [lead.lead_type]           concierge | survival_kit | savings_report | incentive_check | ev_quote | price_audit | newsletter
 * @param {Object} [lead.details]             arbitrary structured answers (quiz, matched EVs, dealer quote…)
 * @param {Object} [lead.analytics]           extra lead_submit params for GA4; nothing personal
 * @returns {Promise<{ok: boolean, queued: boolean, id?: number}>}
 */
export async function submitLead(lead) {
  const payload = {
    email: (lead.email || '').trim(),
    name: lead.name || null,
    phone: lead.phone || null,
    zip: lead.zip || null,
    lead_type: lead.lead_type || 'concierge',
    source_path: typeof window !== 'undefined' ? window.location.pathname + window.location.search : null,
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    details: lead.details || null,
  };

  // Fire an analytics event regardless of backend outcome. Only which fields
  // were filled in goes to GA, never their values; `lead.analytics` adds
  // caller-supplied, non-personal context (form, coarse bands, opt-ins).
  track('lead_submit', {
    ...(lead.analytics || {}),
    lead_type: payload.lead_type,
    has_name: Boolean(payload.name),
    has_zip: Boolean(payload.zip),
    has_phone: Boolean(payload.phone),
    has_note: Boolean(payload.details && payload.details.note),
  });

  try {
    const data = await postLead(payload);
    // Opportunistically flush any previously-queued leads now that we know
    // the backend is up.
    flushQueue();
    track('lead_submit_result', { lead_type: payload.lead_type, queued: false });
    return { ok: true, queued: false, id: data && data.id };
  } catch (e) {
    queueLead(payload);
    track('lead_submit_result', { lead_type: payload.lead_type, queued: true });
    return { ok: true, queued: true };
  }
}

/**
 * Retry any queued leads. Safe to call on app start. Best-effort: a lead that
 * fails again stays queued for the next attempt.
 */
export async function flushQueue() {
  const q = readQueue();
  if (!q.length) return;
  const remaining = [];
  for (const lead of q) {
    try {
      // Strip the local-only marker before sending.
      const { _queuedAt, ...payload } = lead;
      await postLead(payload);
    } catch (e) {
      remaining.push(lead);
    }
  }
  writeQueue(remaining);
}

export const LEADS_ENDPOINT = ENDPOINT;
